import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { getProductBadgeClasses } from '@/lib/capacity-design-system'

// Enhanced Badge component with semantic status variants and capacity management support
const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        success:
          'border-transparent bg-success text-success-foreground hover:bg-success/80',
        warning:
          'border-transparent bg-warning text-warning-foreground hover:bg-warning/80',
        info: 'border-transparent bg-info text-info-foreground hover:bg-info/80',
        production:
          'border-transparent bg-warning text-warning-foreground hover:bg-warning/80',
        // Capacity status variants
        'capacity-optimal':
          'border-transparent bg-capacity-optimal text-white hover:opacity-80',
        'capacity-good':
          'border-transparent bg-capacity-good text-white hover:opacity-80',
        'capacity-warning':
          'border-transparent bg-capacity-warning text-white hover:opacity-80',
        'capacity-critical':
          'border-transparent bg-capacity-critical text-white hover:opacity-80',
        'capacity-overflow':
          'border-transparent bg-capacity-overflow text-white hover:opacity-80 animate-pulse',
        // Product type variants
        'product-fuel':
          'border-transparent bg-product-fuel text-white hover:opacity-80',
        'product-parcel':
          'border-transparent bg-product-parcel text-white hover:opacity-80',
        'product-liquid':
          'border-transparent bg-product-liquid text-white hover:opacity-80',
        'product-bulk':
          'border-transparent bg-product-bulk text-white hover:opacity-80',
        'product-refrigerated':
          'border-transparent bg-product-refrigerated text-white hover:opacity-80',
        'product-hazmat':
          'border-transparent bg-product-hazmat text-white hover:opacity-80',
        'product-general':
          'border-transparent bg-product-general text-white hover:opacity-80',
        // Route geometry status variants
        'geometry-actual':
          'border-transparent bg-emerald-600 text-white hover:bg-emerald-700',
        'geometry-approximate':
          'border-transparent bg-orange-500 text-white hover:bg-orange-600',
        'geometry-missing':
          'border-transparent bg-red-500 text-white hover:bg-red-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant }), className)}
      data-slot="badge"
      {...props}
    >
      {children}
    </div>
  )
}

// Capacity Status Badge - specialized component for capacity utilization
export interface CapacityStatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: 'optimal' | 'good' | 'warning' | 'critical' | 'overflow'
  percentage?: number
  compact?: boolean
}

export function CapacityStatusBadge({
  status,
  percentage,
  compact = false,
  className,
  children,
  ...props
}: CapacityStatusBadgeProps) {
  const variant = `capacity-${status}` as any
  const displayText =
    children ||
    (compact
      ? `${percentage?.toFixed(0) || '0'}%`
      : `${status.charAt(0).toUpperCase() + status.slice(1)}${percentage ? ` (${percentage.toFixed(1)}%)` : ''}`)

  return (
    <Badge
      variant={variant}
      className={cn('font-semibold', className)}
      {...props}
    >
      {displayText}
    </Badge>
  )
}

// Database Product Badge - for database-driven product display
export interface DatabaseProductBadgeProps extends Omit<BadgeProps, 'variant'> {
  product: {
    name: string
    category?: string
    unitType?: string
  }
  compact?: boolean
}

export function DatabaseProductBadge({
  product,
  compact = false,
  className,
  children,
  ...props
}: DatabaseProductBadgeProps) {
  // Use the database-driven badge classes
  const badgeClassName = getProductBadgeClasses(product.category)

  const displayText =
    children ||
    (compact ? product.name : `${product.name} (${product.unitType || 'unit'})`)

  return (
    <div className={cn(badgeClassName, className)} {...props}>
      {displayText}
    </div>
  )
}

// Route Geometry Status Badge - specialized component for route geometry quality
export interface RouteGeometryStatusBadgeProps
  extends Omit<BadgeProps, 'variant'> {
  geometryQuality: 'actual' | 'approximate' | 'missing'
  hasVROOMGeometry?: boolean
  coordinateCount?: number
  showIcon?: boolean
  compact?: boolean
}

export function RouteGeometryStatusBadge({
  geometryQuality,
  hasVROOMGeometry = false,
  coordinateCount,
  showIcon = true,
  compact = false,
  className,
  children,
  ...props
}: RouteGeometryStatusBadgeProps) {
  const variant = `geometry-${geometryQuality}` as any

  // Icon mapping for geometry quality
  const icons = {
    actual: '🛣️',
    approximate: '📐',
    missing: '❌',
  }

  // Text mapping for geometry quality
  const texts = {
    actual: compact ? 'Actual' : 'Actual Road Path',
    approximate: compact ? 'Approx' : 'Straight Lines',
    missing: compact ? 'Missing' : 'No Geometry',
  }

  const icon = showIcon ? icons[geometryQuality] : ''
  const text = texts[geometryQuality]

  // Add coordinate count for actual routes if available
  const displayText =
    children ||
    (coordinateCount && geometryQuality === 'actual' && !compact
      ? `${text} (${coordinateCount.toLocaleString()} pts)`
      : text)

  return (
    <Badge
      variant={variant}
      className={cn('font-semibold', className)}
      title={
        hasVROOMGeometry
          ? `Route uses optimiser geometry with ${coordinateCount || 'unknown'} coordinate points`
          : 'Route uses step-based straight-line approximation'
      }
      {...props}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {displayText}
    </Badge>
  )
}

export { Badge, badgeVariants }
